import React, { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import { Box, Grid, IconButton, useMediaQuery } from "@mui/material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { useTranslation } from "react-i18next";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import ButtonBase from "@mui/material/ButtonBase";
import { criticalData } from "../reports/critical_format";
import QuizzIcon from "../../assets/sidebarIcons/QuizzIcon";
import ReportIcon from "../../assets/sidebarIcons/ReportIcon";
import { useHistory } from "react-router-dom";
import DeleteModal from "./DeleteModal";
import Tooltip from "@mui/material/Tooltip";
import OrgaSelectedControl from "../../components/OrgaSelectedControl";
import {
  getOrganizatonProfiles,
  getOrganizatonMetricsById,
  getOrganizationProfileById,
  launchTeaserReportAndSave,
  setOrganizationMetricsById,
  startEvaluation,
} from "../../redux/actions/organizationProfileCreators";
import { useDispatch } from "react-redux";
import axiosInstance from "../../axios/index";
import RiskProfileIcon from "../../components/icons/RiskProfileIcon";
import RiskProfileControl from "../../components/RiskProfileControl";
import TeaserReportConfirm from "../../components/TeaserReportConfirm";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"; // pour not_started (cercle vide)
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"; // pour in_progress
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // pour done
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder"; // for in_progress (blue)
import WarningIcon from "@mui/icons-material/Warning"; // for failed (red)
import AssignmentIcon from "@mui/icons-material/Assignment";
import RuleIcon from "@mui/icons-material/Rule";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";


// Add these imports at the top of OrgaCard.js

import ConfirmEvaluationModal from "../../components/ConfirmEvaluationModal"; // Adjust path as needed

const DEFAULT_CREDIT_TIER_COSTS = {
  flash_report: 1,
  risk_report_without_validation: 10,
  risk_report_with_validation: 100,
};

const DEFAULT_REPORT_LANGUAGE = "fr";

export default function OrgaCard({
  profile,
  handleOrganizationProfile,
  orgaSelected,
  page,
  searchText,
  status,
  role,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const xs = useMediaQuery("(max-width:400px)");
  const xxs = useMediaQuery("(max-width:360px)");
  const sm = useMediaQuery("(min-width:600px) and (max-width:755px)");
  const md = useMediaQuery("(min-width:900px) and (max-width:1040px)");
  const lg = useMediaQuery("(min-width:1200px) and (max-width:1530px)");

  const [criticalContext, setCriticalContext] = useState({});
  const [open, setOpen] = useState(false);
  const [openControl, setOpenControl] = useState(false);
  const [openRisk, setOpenRisk] = useState(false);
  const storedUser = localStorage.getItem("user");
  let currentUser = {};
  try {
    currentUser = storedUser ? JSON.parse(storedUser) : {};
  } catch (e) {
    currentUser = {};
  }
  const language = localStorage.getItem("i18nextLng");
  const accountType = currentUser?.account_type;
  const [teaserReportOpen, setTeaserReportOpen] = useState(false);

  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [isEvaluationLoading, setIsEvaluationLoading] = useState(false);
  const [reportChoice, setReportChoice] = useState("");
  const [riskModeChoice, setRiskModeChoice] = useState("");
  const [showRiskModeChooser, setShowRiskModeChooser] = useState(false);
  const [pendingEvaluation, setPendingEvaluation] = useState({
    reportType: "",
    withValidation: false,
  });
  const [pendingReportLanguage, setPendingReportLanguage] = useState(DEFAULT_REPORT_LANGUAGE);
  const [apiModalCreditSummary, setApiModalCreditSummary] = useState(null);
  const [isCreditPreviewLoading, setIsCreditPreviewLoading] = useState(false);

  const parseCreditNumber = useCallback((value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }, []);

  const resolveCreditValue = useCallback((...values) => {
    for (const value of values) {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }
    return 0;
  }, []);

  const getReportCreditCostFromTierCosts = useCallback(
    (reportType, withValidation = false, tierCosts = null) => {
      const effectiveTierCosts =
        tierCosts && typeof tierCosts === "object" && Object.keys(tierCosts).length > 0
          ? tierCosts
          : DEFAULT_CREDIT_TIER_COSTS;

      if (reportType === "risk_report") {
        return parseCreditNumber(
          withValidation
            ? effectiveTierCosts?.risk_report_with_validation
            : effectiveTierCosts?.risk_report_without_validation
        );
      }
      if (reportType === "flash_report") {
        return parseCreditNumber(effectiveTierCosts?.flash_report);
      }
      return 0;
    },
    [parseCreditNumber]
  );

  const getReportCreditCost = useCallback(
    (reportType, withValidation = false) => {
      const profileTierCosts =
        profile?.credit_tier_costs &&
        typeof profile.credit_tier_costs === "object" &&
        Object.keys(profile.credit_tier_costs).length > 0
          ? profile.credit_tier_costs
          : profile?.credit?.tier_costs &&
            typeof profile.credit.tier_costs === "object" &&
            Object.keys(profile.credit.tier_costs).length > 0
          ? profile.credit.tier_costs
          : null;

      const userTierCosts =
        currentUser?.credit_tier_costs &&
        typeof currentUser.credit_tier_costs === "object"
          ? currentUser.credit_tier_costs
          : currentUser?.credit?.tier_costs &&
            typeof currentUser.credit.tier_costs === "object"
          ? currentUser.credit.tier_costs
          : null;

      return getReportCreditCostFromTierCosts(
        reportType,
        withValidation,
        profileTierCosts || userTierCosts
      );
    },
    [
      currentUser?.credit?.tier_costs,
      currentUser?.credit_tier_costs,
      getReportCreditCostFromTierCosts,
      profile?.credit?.tier_costs,
      profile?.credit_tier_costs,
    ]
  );

  const normalizeStatus = (value, fallback = "not_started") => {
    const normalized = (value ?? fallback).toString().trim().toLowerCase();

    if (normalized === "in_progress") {
      return "in_progress";
    }

    if (normalized === "not_started" ) {
      return "not_started";
    }

    if (normalized === "failure") {
      return "failed";
    }

    return normalized || fallback;
  };

  const shouldShowWorkflowChooser =
    (profile?.active_report ?? "") === "" &&
    normalizeStatus(profile?.risk_report_status) === "not_started" &&
    normalizeStatus(profile?.flash_report_status) === "not_started";

  const activeReport = (profile?.active_report ?? "").toString().toLowerCase();
  const riskStatus = normalizeStatus(profile?.risk_report_status);
  const flashStatus = normalizeStatus(profile?.flash_report_status);
  const generateReportStatus = normalizeStatus(profile?.generate_report_status, "");
  const hasExplicitValidationStatus =
    profile?.validation_status === true || profile?.validation_status === false;
  const validationStatus = hasExplicitValidationStatus
    ? Boolean(profile?.validation_status)
    : activeReport === "risk_report";

  const isRiskActive = activeReport === "risk_report";
  const isFlashActive = activeReport === "flash_report";
  const isRiskCompleted = riskStatus === "completed";
  const isFlashCompleted = flashStatus === "completed";
  const isGenerateReportCompleted = generateReportStatus === "completed";
  const isRiskWorkflowInProgress =
    riskStatus === "in_progress" && !isGenerateReportCompleted;
  const forceRiskPresentation = isRiskWorkflowInProgress && !isGenerateReportCompleted;
  const isRiskNotStarted = riskStatus === "not_started";
  const isFlashNotStarted = flashStatus === "not_started";
  const showFlashCompletedRiskNotStarted =
    isFlashActive && isFlashCompleted && isRiskNotStarted;
  const showRiskCompletedAutomatic =
    isRiskActive &&
    isGenerateReportCompleted &&
    !validationStatus;
  const canStartRiskFlow = showFlashCompletedRiskNotStarted || showRiskCompletedAutomatic;
  const hideBottomActionForFlash =
    isFlashActive && (flashStatus === "in_progress" || flashStatus === "failed");
  const pendingReportTypeForModal = pendingEvaluation?.reportType || reportChoice;
  const pendingWithValidationForModal = Boolean(pendingEvaluation?.withValidation);
  const remainingCreditsForModal = resolveCreditValue(
    profile?.credit_remaining,
    profile?.credit?.remaining_credits,
    profile?.remaining_credits,
    currentUser?.credit_remaining,
    currentUser?.credit?.remaining_credits
  );
  const reservedCreditsForModal = resolveCreditValue(
    profile?.credit_reserved,
    profile?.credit?.reserved_credits,
    profile?.reserved_credits,
    currentUser?.credit_reserved,
    currentUser?.credit?.reserved_credits
  );
  const availableBeforeForModal = Math.max(
    remainingCreditsForModal - reservedCreditsForModal,
    0
  );
  const reserveCostForModal = getReportCreditCost(
    pendingReportTypeForModal,
    pendingWithValidationForModal
  );
  const availableAfterForModal = Math.max(
    availableBeforeForModal - reserveCostForModal,
    0
  );
  const fallbackModalCreditSummary = pendingReportTypeForModal
    ? {
        reportType: pendingReportTypeForModal,
        withValidation: pendingWithValidationForModal,
        initialCredits: availableBeforeForModal,
        reserveCost: reserveCostForModal,
        remainingAfterReport: availableAfterForModal,
        remaining: remainingCreditsForModal,
        reserved: reservedCreditsForModal,
        availableBefore: availableBeforeForModal,
        availableAfter: availableAfterForModal,
      }
    : null;
  const modalCreditSummary = apiModalCreditSummary || fallbackModalCreditSummary;

  const hasAleValue = profile?.ale_value != null && !isNaN(profile?.ale_value);
  const shouldHideAleCircleForCompletedRiskWithoutAle =
    isRiskActive &&
    isRiskCompleted &&
    isGenerateReportCompleted &&
    !hasAleValue;
  const shouldHideAleCircle =
    isFlashActive || shouldHideAleCircleForCompletedRiskWithoutAle;

  useEffect(() => {
    if (!canStartRiskFlow) {
      setShowRiskModeChooser(false);
      setRiskModeChoice("");
    }
  }, [canStartRiskFlow]);

  const openEvaluationFor = async (e, reportType, withValidation = false) => {
    e.stopPropagation();
    setPendingEvaluation({ reportType, withValidation });
    setPendingReportLanguage(DEFAULT_REPORT_LANGUAGE);
    setApiModalCreditSummary(null);
    setEvaluationModalOpen(true);
    setIsCreditPreviewLoading(true);

    try {
      const response = await axiosInstance.get("/auth/get_user");
      const liveUser = response?.data || {};

      const liveRemaining = resolveCreditValue(
        liveUser?.credit_remaining,
        profile?.credit_remaining,
        profile?.credit?.remaining_credits,
        profile?.remaining_credits,
        currentUser?.credit_remaining,
        currentUser?.credit?.remaining_credits
      );
      const liveReserved = resolveCreditValue(
        liveUser?.credit_reserved,
        profile?.credit_reserved,
        profile?.credit?.reserved_credits,
        profile?.reserved_credits,
        currentUser?.credit_reserved,
        currentUser?.credit?.reserved_credits
      );
      const liveAvailableBefore = Math.max(liveRemaining - liveReserved, 0);
      const liveReserveCost = getReportCreditCostFromTierCosts(
        reportType,
        withValidation,
        liveUser?.credit_tier_costs
      );
      const liveAvailableAfter = Math.max(liveAvailableBefore - liveReserveCost, 0);

      setApiModalCreditSummary({
        reportType,
        withValidation,
        initialCredits: liveAvailableBefore,
        reserveCost: liveReserveCost,
        remainingAfterReport: liveAvailableAfter,
        remaining: liveRemaining,
        reserved: liveReserved,
        availableBefore: liveAvailableBefore,
        availableAfter: liveAvailableAfter,
      });
    } catch (error) {
      setApiModalCreditSummary(null);
    } finally {
      setIsCreditPreviewLoading(false);
    }
  };

  const handleStartEvaluationClick = (e) => {
    e.stopPropagation();
    if (shouldShowWorkflowChooser && !reportChoice) return;
    if (reportChoice === "risk_report") {
      setRiskModeChoice("");
      setShowRiskModeChooser(true);
      return;
    }
    openEvaluationFor(e, reportChoice, false);
  };

  const handleStartRiskModeClick = (e) => {
    e.stopPropagation();
    if (!riskModeChoice) return;
    openEvaluationFor(e, "risk_report", riskModeChoice === "with_validation");
  };

  const handleOpenRiskModeChooser = (e) => {
    e.stopPropagation();
    setRiskModeChoice("");
    setShowRiskModeChooser(true);
  };

  const handleCancelRiskMode = (e) => {
    e.stopPropagation();
    setRiskModeChoice("");
    setShowRiskModeChooser(false);
  };

  const handleNavigateToEvaluation = async () => {
    await dispatch(setOrganizationMetricsById(profile));
    await localStorage.setItem("orgaId", profile?.id);
    history.push("/evaluation");
  };

  const handleNavigateToReport = async () => {
    await dispatch(setOrganizationMetricsById(profile));
    await localStorage.setItem("orgaId", profile?.id);
    history.push("/reports");
  };

  const handleConfirmEvaluation = async () => {
    const reportTypeToStart = pendingEvaluation?.reportType || reportChoice;
    const shouldUseValidation = Boolean(pendingEvaluation?.withValidation);

    if (!reportTypeToStart) {
      return;
    }

    setIsEvaluationLoading(true);
    await dispatch(setOrganizationMetricsById(profile));
    await localStorage.setItem("orgaId", profile?.id);
    try {
      const result = await dispatch(
        startEvaluation(
          profile?.id,
          reportTypeToStart,
          shouldUseValidation,
          pendingReportLanguage
        )
      );
      console.log("[OrgaCard] refresh profiles â†’ params:", {
        page,
        searchText,
        status,
        orgaId: profile?.id,
        reportChoice: reportTypeToStart,
        withValidation: shouldUseValidation,
        reportLanguage: pendingReportLanguage,
      });
      const refreshRes = await dispatch(getOrganizatonProfiles(page, searchText, status));
      console.log("[OrgaCard] refresh profiles â†’ dispatched, result:", refreshRes);
      await dispatch(getOrganizatonMetricsById(profile?.id));
      setEvaluationModalOpen(false);
      setPendingEvaluation({ reportType: "", withValidation: false });
      setPendingReportLanguage(DEFAULT_REPORT_LANGUAGE);
      setApiModalCreditSummary(null);
      setRiskModeChoice("");
      setShowRiskModeChooser(false);
      if (reportTypeToStart === "risk_report" && result) {
        history.push("/evaluation");
        return;
      }
    } finally {
      setIsEvaluationLoading(false);
    }
  };

  const handleCancelEvaluation = () => {
    setEvaluationModalOpen(false);
    setPendingEvaluation({ reportType: "", withValidation: false });
    setPendingReportLanguage(DEFAULT_REPORT_LANGUAGE);
    setApiModalCreditSummary(null);
    setIsCreditPreviewLoading(false);
  };

  //

  const handleEditOrganization = async (profile) => {
    await handleOrganizationProfile(profile, true, true);
    history.push(`/profile`);
  };
  const handlePowerBi = async () => {
    try {
      // await dispatch(getRiskProfile(profile?.id, accountType, language));
      if (accountType !== "free") {
        localStorage.setItem("orgaId", profile?.id);
      }
      history.push(`/risk_profile`);
    } catch (error) {
      if (error?.response?.status === 403) {
        setOpenControl(true);
      }
    }
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const handleCloseControl = () => {
    setOpenControl(false);
  };

  const handleReports = async () => {
    await handleOrganizationProfile(profile, true, true);
    history.push(`/reports`);
  };

  const handleQuizzes = async () => {
    await handleOrganizationProfile(profile, true, true);

    history.push(`/quizzes/get_started`);
  };
  const setterCriticalContext = useCallback(
    (score) => {
      switch (true) {
        case score >= 9:
          setCriticalContext(criticalData["critical"]);
          break;
        case score < 9 && score >= 7:
          setCriticalContext(criticalData["very_high"]);
          break;
        case score < 7 && score >= 5:
          setCriticalContext(criticalData["high"]);
          break;
        case score < 5 && score >= 3:
          setCriticalContext(criticalData["moderate"]);
          break;
        case score < 3:
          setCriticalContext(criticalData["acceptable"]);
          break;
        default:
          break;
      }
    },
    [setCriticalContext]
  );

  function launchTeaserReportHandler() {
    setTeaserReportOpen(true);
  }

  async function handleTeaserReportConfirm() {
    dispatch(
      launchTeaserReportAndSave({ orga_id: profile?.id, confirm: "true" })
    );
    setTeaserReportOpen(false);
    // handleClose();
  }

  useEffect(() => {
    setterCriticalContext(profile?.avg_risk);
  }, [profile?.avg_risk, setterCriticalContext]);

  const reconnaissance_status = profile?.reconnaissance_status || "not_started";
  const diagnostic_status = profile?.diagnostic_status || "not_started";
  const audit_status = profile?.audit_status || "not_started";

  // to be deleted
  const items = [
    { label: "Reconnaissance", status: reconnaissance_status, disabled: false },
    {
      label: "Diagnostic",
      status: diagnostic_status,
      disabled: reconnaissance_status !== "completed",
    },
    {
      label: "Audit",
      status: audit_status,
      disabled: diagnostic_status !== "completed",
    },
  ];

  const getStepLabel = (key) => {
    switch (key) {
      case "reconnaissance_status":
        return language === "fr" ? "Reconnaissance" : "Reconnaissance";
      case "assets_discovery_status":
        return language === "fr" ? "Decouverte des actifs" : "Assets Discovery";
      case "risk_scenarios_status":
        return language === "fr" ? "Identification des risques" : "Risk Identification";
      case "risk_assesment_status":
        return language === "fr" ? "Evaluation des risques" : "Risk Assessment";
      case "generate_report_status":
        return language === "fr" ? "Génération du rapport" : "Report Generation";
      default:
        return key;
    }
  };

  const renderStepStatus = (label, status, options = {}) => {
    const isValidationRequired = options.validationRequired === true;

    const statusConfig = {
      completed: {
        icon: <CheckCircleIcon sx={{ color: "#04ca53", fontSize: 21 }} />,
        color: "#04ca53",
        text: language === "fr" ? "Terminé" : "Completed",
      },
      failed: {
        icon: <WarningIcon sx={{ color: "#f44336", fontSize: 21 }} />,
        color: "#f44336",
        text: language === "fr" ? "Échoué" : "Failed",
      },
      in_progress: {
        icon: <QueryBuilderIcon sx={{ color: "#4890ff", fontSize: 21 }} />,
        color: "#4890ff",
        text: language === "fr" ? "En cours" : "In Progress",
      },
      waiting: {
        icon: <PauseCircleOutlineIcon sx={{ color: "#9827F5", fontSize: 21 }} />,
        color: "#9827F5",
        text: language === "fr" ? "En attente de démarrer" : "Waiting to start",
      },
      not_started: {
        icon: <RadioButtonUncheckedIcon sx={{ color: "#9E9E9E", fontSize: 21 }} />,
        color: "#9E9E9E",
        text: language === "fr" ? "Non démarré" : "Not Started",
      },
      validation_required: {
        icon: <RuleIcon sx={{ color: "#FFC107", fontSize: 21 }} />,
        color: "#FFC107",
        text: language === "fr" ? "Validation Requise" : "Validation Required",
      },
    };

    const config = isValidationRequired
      ? statusConfig.validation_required
      : statusConfig[status] || statusConfig.not_started;

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", columnGap: 0.9 }}>
        <Box sx={{ display: "flex", alignItems: "center", pt: 0.18 }}>
          {config.icon}
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "15.2px",
              fontWeight: 500,
              color: "#132238",
              lineHeight: 1.15,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              mt: 0.35,
              color: config.color,
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: 1.12,
            }}
          >
            {config.text}
          </Typography>
        </Box>
      </Box>
    );
  };

  function getStep() {
    const stepsOrder = [
      "reconnaissance_status",
      "assets_discovery_status",
      "risk_scenarios_status",
      "risk_assesment_status",
      "generate_report_status",
    ];

    const validationStep = validationStatus
      ? stepsOrder.find((key) => {
          const validationKey = key.replace("_status", "_validation");
          return (
            normalizeStatus(profile?.[key], "") === "completed" &&
            profile?.[validationKey] === false
          );
        })
      : null;

    if (validationStep) {
      return renderStepStatus(getStepLabel(validationStep), "validation_required", {
        validationRequired: true,
      });
    }

    if (normalizeStatus(profile?.generate_report_status, "") === "completed") {
      return renderStepStatus(getStepLabel("generate_report_status"), "completed");
    }

    const failedStep = stepsOrder.find(
      (key) => normalizeStatus(profile?.[key], "") === "failed"
    );
    if (failedStep) {
      return renderStepStatus(getStepLabel(failedStep), "failed");
    }

    const inProgressStep = stepsOrder.find(
      (key) => normalizeStatus(profile?.[key], "") === "in_progress"
    );
    if (inProgressStep) {
      return renderStepStatus(getStepLabel(inProgressStep), "in_progress");
    }

    const lastCompletedKey = [...stepsOrder]
      .reverse()
      .find((key) => normalizeStatus(profile?.[key], "") === "completed");

    if (lastCompletedKey) {
      if (lastCompletedKey !== "generate_report_status") {
        const nextKey = stepsOrder[stepsOrder.indexOf(lastCompletedKey) + 1];
        if (nextKey) {
          return renderStepStatus(getStepLabel(nextKey), "waiting");
        }
      }

      return renderStepStatus(getStepLabel(lastCompletedKey), "completed");
    }

    const firstNotStarted = stepsOrder.find(
      (key) => normalizeStatus(profile?.[key]) === "not_started"
    );
    if (firstNotStarted) {
      return renderStepStatus(getStepLabel(firstNotStarted), "not_started");
    }

    return null;
  }
  function formatRoundedValue(value) {
    if (value == null || isNaN(value)) return "N/A";

    const abs = Math.abs(value);

    if (abs >= 1_000_000_000) {
      return `${Math.round(value / 1_000_000_000)}B`;
    }

    if (abs >= 1_000_000) {
      return `${Math.round(value / 1_000_000)}M`;
    }

    if (abs >= 1_000) {
      return `${Math.round(value / 1_000)}k`;
    }

    return Math.round(value).toString();
  }

  // function getButton() {
  //   if (profile?.reconnaissance_status === "not_started") {
  //     return (
  //       <Button
  //         variant="contained"
  //         size="large"
  //         sx={{
  //           bgcolor: "#00a07f",
  //           ":hover": { bgcolor: "#008f6a" },
  //           height: "40px",
  //           width: "100%",
  //           gap: "10px",
  //           color: "00a07f",
  //         }}
  //         startIcon={<PlayCircleOutlineIcon />}
  //         onClick={handleStartEvaluationClick}
  //       >
  //         {t("start_evaluation")}
  //       </Button>
  //     );
  //   } else if (profile?.generate_report_status === "completed") {
  //     return (
  //       <Button
  //         variant="contained"
  //         size="large"
  //         sx={{
  //           bgcolor: "#00a07f",
  //           ":hover": { bgcolor: "#008f6a" },
  //           height: "40px",
  //           width: "100%",
  //           gap: "10px",
  //           color: "00a07f",
  //         }}
  //         startIcon={<ReportIcon color={"white"} />}
  //         onClick={handleNavigateToReport}
  //       >
  //         {t("access_to_risk_report")}
  //       </Button>
  //     );
  //   } else {
  //     return (
  //       <Button
  //         variant="contained"
  //         size="large"
  //         sx={{
  //           bgcolor: "#00a07f",
  //           ":hover": { bgcolor: "#008f6a" },
  //           height: "40px",
  //           width: "100%",
  //           gap: "10px",
  //           color: "00a07f",
  //         }}
  //         startIcon={<AssignmentIcon />}
  //         onClick={handleNavigateToEvaluation}
  //       >
  //         {t("view_evaluation")}
  //       </Button>
  //     );
  //   }
  // }

  const primaryActionSx = {
    bgcolor: "#00a07f",
    ":hover": { bgcolor: "#008f6a" },
    height: { xs: "40px", sm: "42px" },
    width: "100%",
    gap: { xs: "4px", sm: "8px" },
    color: "#f4f4f4",
    fontWeight: 600,
    fontSize: { xs: "12.2px", sm: "14px" },
    textTransform: "none",
    flex: 1,
    minWidth: 0,
    px: { xs: 0.75, sm: 1.25 },
    whiteSpace: { xs: "normal", sm: "nowrap" },
    lineHeight: { xs: 1.05, sm: 1.1 },
    textAlign: "center",
    borderRadius: "9px",
    "& .MuiButton-startIcon": {
      marginRight: { xs: "3px", sm: "5px" },
      marginLeft: 0,
      "& svg": {
        width: { xs: 18, sm: 22 },
        height: { xs: 18, sm: 22 },
      },
    },
    "&.Mui-disabled": {
      bgcolor: "#D2D4D8",
      color: "#A9AFB7",
    },
    "&.Mui-disabled .MuiButton-startIcon": {
      color: "#A9AFB7",
    },
  };

  const secondaryActionSx = {
    ...primaryActionSx,
    bgcolor: "#FCFCFB",
    color: "#111111",
    border: "1px solid #D3D8DE",
    ":hover": {
      bgcolor: "#F6F8FA",
      borderColor: "#BEC8D1",
    },
  };

  const stackedActionsSx = {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: { xs: 1, sm: 1.25 },
    alignItems: "stretch",
  };

  const validationActionsSx = {
    width: "100%",
    display: "grid",
    gridTemplateColumns: { xs: "0.95fr 1.15fr", sm: "repeat(2, minmax(0, 1fr))" },
    gap: { xs: 1, sm: 1.25 },
    alignItems: "stretch",
  };

  const compactPrimaryActionSx = {
    ...primaryActionSx,
    height: { xs: "38px", sm: "40px" },
    fontSize: { xs: "12px", sm: "13.5px" },
    px: { xs: 0.8, sm: 1.1 },
  };

  const compactSecondaryActionSx = {
    ...secondaryActionSx,
    height: { xs: "38px", sm: "40px" },
    fontSize: { xs: "12px", sm: "13.5px" },
    px: { xs: 0.8, sm: 1.1 },
  };

  const multilineSecondaryActionSx = {
    ...secondaryActionSx,
    whiteSpace: "normal",
    lineHeight: 1.06,
    fontSize: { xs: xxs ? "10px" : "10.8px", sm: "12.8px" },
    px: { xs: xxs ? 0.4 : 0.55, sm: 0.9 },
    py: 0.55,
    "& .MuiButton-startIcon": {
      marginRight: "5px",
      marginLeft: 0,
    },
  };

  const splitActionsSx = {
    width: "100%",
    display: "flex",
    gap: { xs: 1, sm: 1.5 },
    flexDirection: "row",
  };

  const isChooserLayout = shouldShowWorkflowChooser || showRiskModeChooser;

  const reportDisplayName = isFlashActive
    ? forceRiskPresentation
      ? validationStatus
        ? "Risk Report With Validation"
        : "Risk Report (Automatic)"
      : "Flash Report"
    : isRiskActive
    ? validationStatus
      ? "Risk Report With Validation"
      : "Risk Report (Automatic)"
    : forceRiskPresentation
    ? validationStatus
      ? "Risk Report With Validation"
      : "Risk Report (Automatic)"
    : "";

  const renderChoiceCard = ({
    value,
    selectedValue,
    onChange,
    title,
    description,
  }) => {
    const selected = selectedValue === value;

    return (
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          onChange(value);
        }}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          borderRadius: "8px",
          px: 0.2,
          py: 0.4,
          minHeight: 0,
          backgroundColor: "transparent",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "#F8FAFC",
          },
        }}
      >
        <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ width: "100%", py: 0.72 }}>
          <Radio
            checked={selected}
            value={value}
            disableRipple
            size="medium"
            sx={{
              p: 0,
              mt: 0.08,
              color: "#B8C2CC",
              "&.Mui-checked": {
                color: "#00a07f",
              },
            }}
          />
          <Box sx={{ textAlign: "left", pr: 0.5 }}>
            <Typography
              sx={{
                fontSize: "14.8px",
                fontWeight: 400,
                color: "#132238",
                lineHeight: 1.14,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: "12.2px",
                color: "#7A8798",
                mt: 0.22,
                lineHeight: 1.24,
              }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>
      </ButtonBase>
    );
  };

  const renderReportStatusBlock = () => {
    if (!activeReport) {
      return getStep();
    }

    if (forceRiskPresentation) {
      return getStep();
    }

    if (isFlashActive) {
      return renderStepStatus(
        getStepLabel("generate_report_status"),
        flashStatus === "completed" ? "completed" : flashStatus
      );
    }

    if (isRiskActive && isGenerateReportCompleted) {
      return renderStepStatus(getStepLabel("generate_report_status"), "completed");
    }

    return getStep();
  };

  const renderCardMainContent = () => {
    if (showRiskModeChooser) {
      return (
        <Stack spacing={1.05} sx={{ width: "100%" }}>
          <Typography
            sx={{
              fontSize: "14.3px",
              fontWeight: 400,
              color: "#132238",
              lineHeight: 1.22,
            }}
          >
            Choose Risk Report Mode
          </Typography>
          <Stack spacing={1.05}>
            {renderChoiceCard({
              value: "automatic",
              selectedValue: riskModeChoice,
              onChange: setRiskModeChoice,
              title: "Automatic",
              description: "Runs without user interaction (~48h)",
            })}
            {renderChoiceCard({
              value: "with_validation",
              selectedValue: riskModeChoice,
              onChange: setRiskModeChoice,
              title: "With Validation",
              description: "You validate each step before continuing",
            })}
          </Stack>
        </Stack>
      );
    }

    if (shouldShowWorkflowChooser) {
      return (
        <Stack spacing={1.05} sx={{ width: "100%" }}>
          <Typography
            sx={{
              fontSize: "14.3px",
              fontWeight: 400,
              color: "#132238",
              lineHeight: 1.22,
            }}
          >
            What type of report do you want to start ?
          </Typography>
          <Stack spacing={1.05}>
            {renderChoiceCard({
              value: "flash_report",
              selectedValue: reportChoice,
              onChange: setReportChoice,
              title: "Flash Report",
              description: "Quick assessment in around 10 minutes",
            })}
            {renderChoiceCard({
              value: "risk_report",
              selectedValue: reportChoice,
              onChange: setReportChoice,
              title: "Risk Report",
              description: "In-depth assessment with detailed analysis",
            })}
          </Stack>
        </Stack>
      );
    }

    if (!reportDisplayName) {
      return getStep();
    }

    return (
      <Stack spacing={0}>
        <Typography
          variant="title5"
          sx={{
            fontSize: "20px",
            fontWeight: 500,
            color: "#121828",
            lineHeight: 1.22,
          }}
        >
          {reportDisplayName}
        </Typography>
        <Box sx={{ mt: 2.35 }}>
          {renderReportStatusBlock()}
        </Box>
      </Stack>
    );
  };

  const renderBottomActions = () => {
    if (showRiskModeChooser) {
      return (
        <Box sx={splitActionsSx}>
          <Button
            variant="outlined"
            size="large"
            sx={compactSecondaryActionSx}
            onClick={handleCancelRiskMode}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={!riskModeChoice}
            sx={compactPrimaryActionSx}
            startIcon={<PlayCircleOutlineIcon />}
            onClick={handleStartRiskModeClick}
          >
            Start
          </Button>
        </Box>
      );
    }

    if (shouldShowWorkflowChooser) {
      return (
        <Button
          variant="contained"
          size="large"
          disabled={!reportChoice}
          sx={compactPrimaryActionSx}
          startIcon={<PlayCircleOutlineIcon />}
          onClick={handleStartEvaluationClick}
        >
          Start
        </Button>
      );
    }

    if (isRiskWorkflowInProgress) {
      return (
        <Button
          variant="contained"
          size="large"
          sx={primaryActionSx}
          startIcon={<AssignmentIcon />}
          onClick={handleNavigateToEvaluation}
        >
          {t("view_evaluation")}
        </Button>
      );
    }

    if (showFlashCompletedRiskNotStarted) {
      return (
        <Box sx={validationActionsSx}>
          <Button
            variant="contained"
            size="large"
            sx={primaryActionSx}
            startIcon={<ReportIcon color={"white"} />}
            onClick={(e) => {
              e.stopPropagation();
              handleReports();
            }}
          >
            Access Report
          </Button>
          <Button
            variant="outlined"
            size="large"
            sx={secondaryActionSx}
            startIcon={<PlayCircleOutlineIcon />}
            onClick={handleOpenRiskModeChooser}
          >
            Start Risk Report
          </Button>
        </Box>
      );
    }

    if (isRiskActive && isGenerateReportCompleted) {
      if (validationStatus) {
        return (
          <Button
            variant="contained"
            size="large"
            sx={primaryActionSx}
            startIcon={<ReportIcon color={"white"} />}
            onClick={(e) => {
              e.stopPropagation();
              handleReports();
            }}
          >
            Access Report
          </Button>
        );
      }

      return (
        <Box sx={stackedActionsSx}>
          <Button
            variant="contained"
            size="large"
            sx={primaryActionSx}
            startIcon={<ReportIcon color={"white"} />}
            onClick={(e) => {
              e.stopPropagation();
              handleReports();
            }}
          >
            Access Report
          </Button>
          <Button
            variant="outlined"
            size="large"
            sx={multilineSecondaryActionSx}
            startIcon={!xs ? <PlayCircleOutlineIcon /> : null}
            onClick={(e) => openEvaluationFor(e, "risk_report", true)}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: 1.05,
              }}
            >
              <Box component="span">Start Risk Report</Box>
              <Box component="span">With Validation</Box>
            </Box>
          </Button>
        </Box>
      );
    }

    if (isFlashActive && isFlashCompleted) {
      return (
        <Button
          variant="contained"
          size="large"
          sx={primaryActionSx}
          startIcon={<ReportIcon color={"white"} />}
          onClick={(e) => {
            e.stopPropagation();
            handleReports();
          }}
        >
          Access Report
        </Button>
      );
    }

    if (isGenerateReportCompleted) {
      return (
        <Button
          variant="contained"
          size="large"
          sx={primaryActionSx}
          startIcon={<ReportIcon color={"white"} />}
          onClick={(e) => {
            e.stopPropagation();
            handleReports();
          }}
        >
          Access Report
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        size="large"
        sx={primaryActionSx}
        startIcon={<AssignmentIcon />}
        onClick={handleNavigateToEvaluation}
      >
        {t("view_evaluation")}
      </Button>
    );
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case "not_started":
        return <RadioButtonUncheckedIcon sx={{ color: "#9E9E9E" }} />;
      case "pending":
        return <HourglassEmptyIcon sx={{ color: "#FF9800" }} />;
      case "completed":
        return <CheckCircleIcon sx={{ color: "#4CAF50" }} />;
      default:
        return <RadioButtonUncheckedIcon sx={{ color: "#9E9E9E" }} />;
    }
  };

  return (
    <>
      <Card
        sx={{
          backgroundColor: "#FFFFFF",
          boxShadow: "none",
          border:
            orgaSelected?.id === profile?.id
              ? "2.7px solid #78C3AE"
              : "1px solid #EEF2F5",
          borderRadius: "14px",
          cursor: "pointer",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        onClick={() => {
          !open && handleOrganizationProfile(profile, false, true);
        }}
      >
        <CardContent
          sx={{
            px: { xs: 1.75, sm: 2.1 },
            pt: { xs: 1.75, sm: 2 },
            pb: { xs: isChooserLayout ? 1.15 : 1.7, sm: isChooserLayout ? 1.35 : 1.9 },
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
	          <Stack
	            direction="row"
	            spacing={1}
	            alignItems="center"
	            justifyContent="space-between"
	          >
            <Stack direction="row" spacing={1} alignItems="center">
              {/* <Avatar
              alt="Remy Sharp"
              src={profile?.logo}
              sx={{ width: 29, height: 29 }}
            /> */}
		              <Typography
		                variant="title5"
		                component="div"
		                sx={{
		                  fontSize: "24px",
		                  // textTransform: "capitalize",
		                  fontFamily: 'Barlow Condensed',
		                  color: "#121828",
		                  fontWeight: 500,
		                }}
		              >
                {profile?.name_A}
              </Typography>
              {/*{profile ? (*/}
              {/*    <CompletedOrga height="16" width="16"/>*/}
              {/*) : (*/}
              {/*    <Incompleted height="16" width="16"/>*/}
              {/*)}*/}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {/* <IconButton
                size="small"
                sx={{
                  background: "#E3E5E5",
                  borderRadius: "5px",
                }}
                // onClick={(e) => {
                //   e.stopPropagation();
                //   handlePowerBi();
                // }}
              >
                <RiskProfileIcon color={"#757575"} />
              </IconButton> */}

              {/* <IconButton
                size="small"
                sx={{
                  background: "#66CDAA",
                  borderRadius: "5px",
                  "&:hover": {
                    backgroundColor: "#5CBF99",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReports(); // use this for scanning action
                }}
              >
                <ReportIcon color="#E3E5E5" />
              </IconButton> */}

              {/*{(role === "enterprise_admin" || (role === "cytailor_admin" && !profile?.associated && !profile?.orga_completed)) && (orgaSelected?.id === profile?.id ? (*/}
              {/* {(role === "enterprise_admin" ||
                (role === "cytailor_admin" && !profile?.associated)) &&
                (orgaSelected?.id === profile?.id ? (
                  <Tooltip title={t("cannot_delete")}>
                    <span>
                      {" "}
                      <IconButton
                        size="small"
                        disabled
                        sx={{ background: "#B20306", borderRadius: "5px" }}
                      >
                        <DeleteForeverOutlinedIcon sx={{ color: "#B20306" }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <IconButton
                    size="small"
                    sx={{
                      background: "#B20306",
                      borderRadius: "5px",
                      "&:hover": {
                        backgroundColor: "#8C0204",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(true);
                      dispatch(getOrganizationProfileById(profile?.id));
                    }}
                  >
                    <DeleteForeverOutlinedIcon sx={{ color: "#f4f4f4" }} />
                  </IconButton>
                ))} */}
            </Stack>
          </Stack>
			          <Grid container spacing={isChooserLayout ? 1 : 1.6} mt={isChooserLayout ? 0.85 : 1.35}>
			            <Grid item xs={!shouldShowWorkflowChooser && !showRiskModeChooser && !shouldHideAleCircle ? 8 : 12} sm={!shouldShowWorkflowChooser && !showRiskModeChooser && !shouldHideAleCircle ? 8 : 12}>
		              <Box
		                sx={{
		                  minHeight: isChooserLayout ? 88 : 74,
		                  flexGrow: 1,
		                  display: "flex",
		                  alignItems: "flex-start",
	                }}
	              >
                {renderCardMainContent()}
              </Box>
            </Grid>
            {!shouldShowWorkflowChooser && !showRiskModeChooser && !shouldHideAleCircle && (
	            <Grid item xs={4} sm={4}>
		              <Box
		                sx={{
		                  width: { xs: 90, sm: 112 },
		                  height: { xs: 90, sm: 112 },
		                  borderRadius: "50%",
		                  border: "2px solid #00a07f",
		                  display: "flex",
		                  alignItems: "center",
		                  justifyContent: "center",
	                    mx: "auto",
		                }}
	              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
		                  <Typography
		                    variant="subtitle2"
		                    sx={{
		                      color: "primary.main",
		                      fontWeight: "bold",
		                      fontSize: { xs: "14px", sm: "18px" },
		                      textAlign: "center",
		                      lineHeight: 1.1,
		                    }}
		                  >
                    {profile?.ale_value != null && !isNaN(profile?.ale_value) ? (
                      <>
                        {formatRoundedValue(profile?.ale_value)} {profile?.current_currency}
                      </>
                    ) : (
                      "N/A"
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          </Grid>
        </CardContent>
	        {!hideBottomActionForFlash && (
	          <CardActions sx={{
	              px: { xs: 1.5, sm: 2 },
	              pb: { xs: isChooserLayout ? 1.25 : 1.45, sm: isChooserLayout ? 1.45 : 1.7 },
	              pt: isChooserLayout ? 0.35 : 0.5,
	              alignItems: "stretch",
                mt: "auto",
	            }}>
            {/* <Button
            variant="contained"
            color="cardButton"
            size="small"
            sx={{ height: "40px", width: "85%", gap: "10px", color: "#4A4A6A" }}
            onClick={(e) => {
              // profile?.tabs_completed >= 5
              handleQuizzes();
              // : setOpenControl(true);
              e.stopPropagation();
            }}
          >
            <QuizzIcon color="#4A4A6A" />
            {t("quizzes")}
            {}
          </Button>
          <Button
            variant="contained"
            color="cardButton"
            size="small"
            sx={{
              height: "40px",
              width: "85%",
              gap: "10px",
              color: "#4A4A6A",
            }}
            onClick={(e) => {
              // profile?.tabs_completed >= 5
              handleReports();
              // : setOpenControl(true);
              e.stopPropagation();
            }}
          >
            <ReportIcon color="#4A4A6A" />
            {t("reports")}
            {}
          </Button> */}

            {/* <Button
      variant="contained"
      color="button"
      size="large"
      sx={{ height: "40px", width: "100%", gap: "10px", color: "#f4f4f4" }}
      onClick={(e) => {
        e.stopPropagation();
        handleQuizzes(); // Routes to quizz page
      }}
    >
      
      {t("start_quizz")}
    </Button> */}

            {/* {getButton()} */}
            {renderBottomActions()}
          </CardActions>
        )}
        <DeleteModal
          open={open}
          handleClose={handleClose}
          profile={profile}
          page={page}
          searchText={searchText}
          status={status}
          handleModalClick={handleModalClick}
          role={role}
        />
        <OrgaSelectedControl
          open={openControl}
          handleClose={handleCloseControl}
          type={"not_completed"}
        />
        <RiskProfileControl
          open={openRisk}
          handleClose={() => setOpenRisk(false)}
        />

        <TeaserReportConfirm
          open={teaserReportOpen}
          onClose={() => setTeaserReportOpen(false)}
          onConfirm={handleTeaserReportConfirm}
          message={t("teaser_report_confirm_message")}
          cancelText={t("refuse")}
          confirmText={t("confirm")}
          title={language === "fr" ? "Es-tu sÃ»r?" : "Are you sure?"}
          // onConfirm={handleTeaserReportConfirm}
        />
      </Card>
      <ConfirmEvaluationModal
        open={evaluationModalOpen}
        onClose={handleCancelEvaluation}
        onConfirm={handleConfirmEvaluation}
        loading={isEvaluationLoading}
        creditLoading={isCreditPreviewLoading}
        creditSummary={modalCreditSummary}
        reportLanguage={pendingReportLanguage}
        onReportLanguageChange={setPendingReportLanguage}
      />
    </>
  );
}
